<template>
  <AppContainer>
    <form
      autocomplete="off"
      @submit.prevent="submitForm"
    >
      <AppCard>
        <TextInput
          v-model.trim="formData.shortName"
          v-focus
          label="Short Name"
          required
          :validation-error="hasValidationError('shortName') ? getValidationError('shortName') : null"
        />

        <TextInput
          v-model.trim="formData.name"
          v-focus
          label="Name"
          required
          :validation-error="hasValidationError('name') ? getValidationError('name') : null"
        />

        <TextInput
          v-model.trim="formData.sortOrder"
          v-focus
          type="number"
          label="Sort Order"
          :validation-error="hasValidationError('sortOrder') ? getValidationError('sortOrder') : null"
        />

        <TextInput
          v-model.trim="formData.url"
          v-focus
          label="Framework URL"
          required
          :validation-error="hasValidationError('url') ? getValidationError('url') : null"
        />

        <ImageFileInput
          class="max-w-md"
          label="Framework Icon"
          :validation-error="hasValidationError('icon') ? getValidationError('icon') : null"
          :preview="model?.icon"
          :required="!icon && !model?.icon"
          @change="icon = $event.target.files[0] || null"
        />

        <TextEditorInput
          id="write-up"
          v-model="formData.writeUp"
          label="Framework write-up"
          :validation-error="hasValidationError('writeUp') ? getValidationError('writeUp') : null"
          required
        />

        <template #buttons>
          <ButtonSubmitRow
            :cancel-route="{ name: 'app_framework_list' }"
            :processing="isSubmitting"
          />
        </template>
      </AppCard>
    </form>
  </AppContainer>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';

import { AppContainer } from '@/components/app';
import { AppCard } from '@/components/cards';
import { ButtonSubmitRow } from '@/components/buttons';
import { ImageFileInput, TextEditorInput, TextInput } from '@/components/form-inputs';

import { useFormSubmit, useHttp } from '@/composables';

const props = defineProps({
  model: {
    type: Object,
    default: () => null,
  },
});

const { submit, isSubmitting, hasValidationError, getValidationError } = useFormSubmit();
const router = useRouter();

const formData = reactive({
  name: props.model?.name || null,
  sortOrder: props.model?.sortOrder || null,
  shortName: props.model?.shortName || null,
  url: props.model?.url || null,
  writeUp: props.model?.writeUp || null,
});
const icon = ref(null);

const submitForm = async () => {
  const frameworkResponse = await submit(props.model?.id, formData, null, 'frameworks');

  if (frameworkResponse.statusCode === HttpStatusCode.Ok) {
    if (icon.value) {
      isSubmitting.value = true;

      const iconResponse = await uploadIcon(frameworkResponse.data.id);

      // If there was an error uploading the icon, prevent the user from being redirected to the framework list page
      if (iconResponse.statusCode !== HttpStatusCode.Ok) {
        isSubmitting.value = false;

        return;
      }
    }

    await router.push({ name: 'app_framework_list' });
  }

  isSubmitting.value = false;
};

const uploadIcon = async frameworkId => {
  const data = new FormData();

  data.set('icon', icon.value);

  return await useHttp().post(`/frameworks/${frameworkId}/icon`, data);
};
</script>
